/**
 * The intensity director (FS-08, slice). Owns Intensity (0..1) and the resulting speed tier, and
 * lerps scroll speed rather than stepping it (Design Bible §3). For Infinite Frequency the intensity
 * floor rises slowly so difficulty climbs; movements are a readable label over that climb.
 */
import { SPEED_LERP_TIME, SPEED_TIERS } from '../core/constants';
import { clamp, lerp } from '../core/math-det';
import type { EventBus } from '../core/events';
import type { EventMap, MovementName } from '../core/event-map';

const MOVEMENTS: { name: MovementName; dur: number; peak: number }[] = [
  { name: 'Intro', dur: 16, peak: 0.22 },
  { name: 'Build', dur: 22, peak: 0.5 },
  { name: 'Drop', dur: 26, peak: 0.85 },
  { name: 'Breakdown', dur: 16, peak: 0.45 },
  { name: 'Finale', dur: 30, peak: 1.0 },
];

export class Director {
  time = 0;
  intensity = 0.15;
  scrollSpeed = SPEED_TIERS[0]!.speed;
  tier = 1;
  overdrive = false;
  /**
   * Infinite Frequency's endless ramp (FS-09 R21) — added on top of the per-segment intensity
   * curve, one step per full loop of the content plan, never per elapsed time or frame count.
   * Deliberately NOT cleared by `reset()`, which runs on every segment change: this is a
   * cross-segment, cross-loop accumulator that only `Game.beginRun` (a fresh run) zeroes.
   */
  endlessBonus = 0;
  private movementIndex = -1;
  private cycleLength = MOVEMENTS.reduce((a, m) => a + m.dur, 0);

  constructor(private bus: EventBus<EventMap>) {}

  reset(): void {
    this.time = 0;
    this.intensity = MOVEMENTS[0]!.peak * 0.6;
    this.scrollSpeed = SPEED_TIERS[0]!.speed;
    this.tier = 1;
    this.overdrive = false;
    this.movementIndex = -1;
  }

  setOverdrive(on: boolean): void {
    this.overdrive = on;
  }

  step(dt: number): void {
    this.time += dt;

    // A rising floor over cycles (endless climb), plus the current movement's shape.
    const cycles = Math.floor(this.time / this.cycleLength);
    const floor = clamp(0.12 + cycles * 0.12, 0, 0.85);

    let tInCycle = this.time % this.cycleLength;
    let idx = 0;
    let acc = 0;
    let phase = 0;
    for (let i = 0; i < MOVEMENTS.length; i++) {
      const m = MOVEMENTS[i]!;
      if (tInCycle < acc + m.dur) {
        idx = i;
        phase = (tInCycle - acc) / m.dur;
        break;
      }
      acc += m.dur;
    }
    const m = MOVEMENTS[idx]!;
    // Ease within the movement toward its peak and back a touch.
    const shape = Math.sin(Math.min(phase, 1) * Math.PI) * 0.5 + 0.5 * Math.min(phase * 2, 1);
    this.intensity = clamp(Math.max(floor, m.peak * (0.55 + 0.45 * shape)) + this.endlessBonus, 0, 1);

    if (idx !== this.movementIndex) {
      this.movementIndex = idx;
      this.bus.emit('movement:changed', { movement: m.name, intensity: this.intensity });
    }

    // Target speed from intensity → tier, lerped.
    let targetTier = SPEED_TIERS[0]!;
    for (let i = 0; i < SPEED_TIERS.length; i++) {
      if (this.intensity >= SPEED_TIERS[i]!.minIntensity) targetTier = SPEED_TIERS[i]!;
    }
    let targetSpeed = targetTier.speed;
    let newTier = targetTier.tier;
    if (this.overdrive) {
      // Overdrive bumps the tier by one.
      const bumped = SPEED_TIERS[Math.min(targetTier.tier, SPEED_TIERS.length - 1)]!;
      targetSpeed = bumped.speed;
      newTier = bumped.tier;
    }
    const k = clamp(dt / SPEED_LERP_TIME, 0, 1);
    this.scrollSpeed = lerp(this.scrollSpeed, targetSpeed, k);
    if (newTier !== this.tier) {
      this.tier = newTier;
      this.bus.emit('speed:tier', { tier: newTier });
    }
  }

  get movementName(): MovementName {
    return MOVEMENTS[Math.max(0, this.movementIndex)]!.name;
  }
}
